import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useApp } from "@/context/AppContext";
import { PageHeader, Panel, Field, NativeSelect } from "@/components/kit";
import { errMsg } from "@/lib/format";
import { OnlinePaymentPanel, SmsPanel, SecurityPanel, ReceiptReminderPanel } from "@/components/SettingsPanels";

const FIELDS = ["name", "matricule_fiscal", "address", "city", "postal_code", "phone", "email", "activity", "rne", "bank_rib"];

export default function Settings() {
  const { api, t, company, refreshCompany, can } = useApp();
  const [f, setF] = useState(null);
  const [logo, setLogo] = useState(null);
  const ref = useRef();
  const rw = can("settings", true);
  useEffect(() => { setF({ ...company, settings: { ...company.settings } }); }, [company]);
  useEffect(() => {
    if (!company.logo_file_id) return;
    let url;
    api.get(`/files/${company.logo_file_id}`, { responseType: "blob" }).then((r) => { url = URL.createObjectURL(r.data); setLogo(url); }).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [api, company.logo_file_id]);
  if (!f) return null;
  const s = f.settings || {};
  const setS = (k, v) => setF({ ...f, settings: { ...s, [k]: v } });
  const save = async () => {
    try {
      const body = Object.fromEntries(FIELDS.map((k) => [k, f[k] || ""]));
      await api.put("/company", { ...body, settings: s, support_access: f.support_access });
      await refreshCompany();
      toast.success(t("saved"));
    } catch (e) { toast.error(errMsg(e)); }
  };
  const upload = async (e) => {
    const fd = new FormData();
    fd.append("file", e.target.files[0]);
    try { await api.post("/company/logo", fd); await refreshCompany(); toast.success(t("logo")); } catch (err) { toast.error(errMsg(err)); }
  };
  const lab = (k) => ({ matricule_fiscal: t("mf"), activity: "Activité", rne: "Identifiant RNE", bank_rib: "RIB bancaire" }[k] || t(k));
  return (
    <>
      <PageHeader eyebrow={company.name} title={t("settings")}>{rw && <Button onClick={save} data-testid="settings-save">{t("save")}</Button>}</PageHeader>
      <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
        <Panel title={t("company_info")}>
          <div className="grid gap-4 sm:grid-cols-2">
            {FIELDS.map((k) => (
              <Field key={k} label={lab(k)} className={k === "name" || k === "address" ? "sm:col-span-2" : ""}>
                <Input disabled={!rw} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`settings-${k}`} />
              </Field>
            ))}
          </div>
        </Panel>
        <div className="space-y-6">
          <Panel title={t("logo")}>
            <div className="flex items-center gap-4">
              <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-lg border bg-slate-50">{logo ? <img src={logo} alt="logo" className="max-h-full max-w-full object-contain" data-testid="settings-logo-img" /> : <span className="text-xs text-slate-400">—</span>}</div>
              <input ref={ref} type="file" accept="image/*" hidden onChange={upload} data-testid="settings-logo-input" />
              {rw && <Button variant="outline" onClick={() => ref.current.click()} data-testid="settings-logo-btn"><Upload className="h-4 w-4 me-1" />{t("logo")}</Button>}
            </div>
          </Panel>
          <Panel title={t("pdf_template")}>
            <div className="space-y-4">
              <Field label={t("pdf_color")}><div className="flex items-center gap-2"><input type="color" value={s.pdf_color || "#E30613"} onChange={(e) => setS("pdf_color", e.target.value)} disabled={!rw} data-testid="settings-pdf-color" className="h-9 w-14 rounded border" /><span className="num text-xs">{s.pdf_color}</span></div></Field>
              <Field label={t("pdf_lang")}><NativeSelect value={s.pdf_lang || "fr"} onChange={(e) => setS("pdf_lang", e.target.value)} disabled={!rw} data-testid="settings-pdf-lang"><option value="fr">Français</option><option value="ar">العربية</option></NativeSelect></Field>
              <Field label={t("pdf_mentions")}><Textarea value={s.pdf_mentions || ""} onChange={(e) => setS("pdf_mentions", e.target.value)} disabled={!rw} data-testid="settings-pdf-mentions" /></Field>
            </div>
          </Panel>
          <Panel title={<span>{t("ttn_mode")} <span className="ms-1 rounded bg-orange-100 px-1.5 text-[10px] font-bold text-orange-800">{t("simulated")}</span></span>}>
            <NativeSelect className="w-full" value={s.ttn_mode || "normal"} onChange={(e) => setS("ttn_mode", e.target.value)} disabled={!rw} data-testid="settings-ttn-mode">
              {["normal", "technical", "reject"].map((m) => <option key={m} value={m}>{t(`ttn_${m}`)}</option>)}
            </NativeSelect>
            <p className="mt-2 text-xs text-slate-500">Permet de démontrer les nouvelles tentatives automatiques et la correction guidée en cas de rejet.</p>
          </Panel>
          <SecurityPanel />
          <OnlinePaymentPanel rw={rw} />
          <SmsPanel s={s} setS={setS} rw={rw} />
          <ReceiptReminderPanel s={s} setS={setS} rw={rw} />
          {company.role === "owner" && (
            <Panel>
              <label className="flex items-center gap-3 text-sm"><Switch checked={!!f.support_access} onCheckedChange={(v) => setF({ ...f, support_access: v })} data-testid="settings-support-access" />{t("support_access")}</label>
              <p className="mt-2 text-xs text-slate-500">Chaque accès du support est tracé dans le journal d'audit.</p>
            </Panel>
          )}
        </div>
      </div>
    </>
  );
}
